// src/components/SharePage.tsx
import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { SharetribeRepository } from "../infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { CoachCard } from "./ui/CoachCard";
import { ProductCard } from "./ui/ProductCard";
import { SwimmerCard } from "./ui/SwimmerCard";
import { FeedList } from "./ui/FeedList";
import { AddPostDialog } from "./ui/AddPostDialog";
import { Listing, IncludedItem, CreatePostForm, CoachProfileData } from "../types";
import { PostRepository } from "@/infrastructure/repositories/PostRepository";
import { MessagesPage } from "./MessagesPage";
import { ConnectedUser } from "../types/index";
import { TeamsSection } from "./TeamsSection";
import { CoachProfile } from "./CoachProfile";
import { secureStorage } from "@/utils/secureStorage";


export function SharePage({ forcedTab }: { forcedTab?: string }) {
  const tabTitles: Record<string, string> = {
    team: "Teams",
    feed: "Feed",
    messages: "Messages",
    find: "Find",
  };

  const [activeTab, setActiveTab] =
    useState<"team" | "feed" | "messages" | "find">("find");

  const [findFilter, setFindFilter] =
    useState<"coaches" | "products" | "swimmers">("coaches");

  const [listings, setListings] = useState<Listing[]>([]);
  const [included, setIncluded] = useState<IncludedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterChanged, setFilterChanged] = useState(false);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const repo = new SharetribeRepository(apiService);

  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);

  const [showPostDialog, setShowPostDialog] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [forceReloadFeed, setForceReloadFeed] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<any>(null);

  const userType = secureStorage.get("user_type") || "swimmer";
  const swimmerId = String(localStorage.getItem("current_user_swimmer_id") || "");
  const current_user_id = swimmerId;
  const [connectedIds, setConnectedIds] = useState<ConnectedUser[]>([]);

  // -------------------------------------------------------
  // Load connected list
  // -------------------------------------------------------
  useEffect(() => {
    const loadConnected = async () => {
      const userType = secureStorage.get("user_type") || "swimmer";

      let mySwimmerId = Number(localStorage.getItem("current_user_swimmer_id"));
      let myCoachId = Number(localStorage.getItem("coach_id"));

      if (userType === "coach" && !myCoachId) {
        myCoachId = mySwimmerId;
      }

      let list: any[] = [];

      if (userType === "coach") list = await repo.getCoachSwimmers(myCoachId, 2);
      else list = await repo.getSwimmerCoaches(mySwimmerId, 2);

      const connected = list.map((x) => ({
        id: String(x.id),
        coach_id: x.coach_id ? String(x.coach_id) : undefined,
        parent_swimmer_id: x.parent_swimmer_id ? String(x.parent_swimmer_id) : undefined,
      }));

      setConnectedIds(connected);
    };

    if (activeTab === "find" && findFilter === "swimmers") {
      loadConnected();
    }
  }, [activeTab, findFilter]);


  useEffect(() => {
    if (forcedTab) {
      setActiveTab(forcedTab as any);
    }
  }, [forcedTab]);


  // -------------------------------------------------------
  // Create Post
  // -------------------------------------------------------
  const postRepo = new PostRepository();

  const handleCreatePost = async ({ text, mediaFile }: CreatePostForm) => {
    try {
      const userId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
      const coachId = Number(localStorage.getItem("coach_id")) || 0;

      let photoUrl = "";
      let videoUrl = "";

      if (mediaFile && mediaFile.type.startsWith("image/")) {
        const res = await postRepo.uploadFeedImage(mediaFile);
        if (res.status === 1) photoUrl = res.image_link;
      }

      if (mediaFile && mediaFile.type.startsWith("video/")) {
        const res = await postRepo.uploadFeedVideo(mediaFile);
        if (res.status === 1) videoUrl = res.video_link;
      }

      const res = await postRepo.publishFeed(
        userId,
        coachId,
        text,
        photoUrl,
        videoUrl
      );

      if (res === 1) {
        setShowPostDialog(false);

        if (activeTab === "feed") {
          setTimeout(() => {
            window.scrollTo(0, 0);
            setForceReloadFeed((v) => v + 1);
          }, 300);
        }
      } else {
        alert("Failed to publish post");
      }
    } catch (err) {
      alert("Error publishing post");
    }
  };


  // -------------------------------------------------------
  // Optimized debounce for filter/search change
  // -------------------------------------------------------
  const DEBOUNCE_MS = 350;
  const filterSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (activeTab !== "find") return;

    if (filterSearchTimeoutRef.current)
      clearTimeout(filterSearchTimeoutRef.current);

    filterSearchTimeoutRef.current = setTimeout(() => {
      setHasMore(true);
      setPage(1);
      setFilterChanged(true);
    }, DEBOUNCE_MS);

    return () => {
      if (filterSearchTimeoutRef.current)
        clearTimeout(filterSearchTimeoutRef.current);
    };
  }, [findFilter, searchQuery, activeTab]);


  // -------------------------------------------------------
  // Throttled network requests + cancel stale results
  // -------------------------------------------------------
  const THROTTLE_MS = 500;
  const lastListingsRequestRef = useRef(0);
  const latestRequestRef = useRef(0);

  const waitForThrottle = async () => {
    const now = Date.now();
    const elapsed = now - lastListingsRequestRef.current;
    if (elapsed < THROTTLE_MS) {
      await new Promise((resolve) =>
        setTimeout(resolve, THROTTLE_MS - elapsed)
      );
    }
    lastListingsRequestRef.current = Date.now();
  };

const dedupeById = (arr: any[]) => {
  const map = new Map<string, any>();
  arr.forEach((item) => {
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
};

  // -------------------------------------------------------
  // Load listings
  // -------------------------------------------------------
  const loadListings = async (
    pageNo: number,
    opts?: { reset?: boolean; filterSnapshot?: typeof findFilter; searchSnapshot?: string }
  ) => {
    const { reset = false } = opts || {};
    const filterSnapshot = opts?.filterSnapshot ?? findFilter;
    const searchSnapshot = opts?.searchSnapshot ?? searchQuery;

    if (!reset && (loading || !hasMore)) return;

    setLoading(true);
    await waitForThrottle();

    const token = Date.now();
    latestRequestRef.current = token;

    try {
      // Swimmer listing
      if (filterSnapshot === "swimmers") {
        const uid =
          localStorage.getItem("selected_swimmer_id") ||
          localStorage.getItem("current_user_swimmer_id");

        const userId = Number(uid) || 0;

        const swimmers = await repo.findSwimmers(
          userId,
          0,
          searchSnapshot,
          pageNo
        );

        if (latestRequestRef.current !== token) return;

       setListings((prev) => {
          const merged = pageNo === 1 || reset
            ? swimmers
            : [...prev, ...swimmers];

          return dedupeById(merged);
        });


        if (swimmers.length === 0) setHasMore(false);

        return;
      }

      // Coaches / products listing
      const type = filterSnapshot === "products" ? "product" : "service";

      const params = {
        pub_listingType: type,
        keywords: searchSnapshot || "",
        page: pageNo.toString(),
        per_page: "20",
      };

      const response = await repo.listingsQuery(params, [
        "author.profileImage",
        "images",
      ]);

      if (latestRequestRef.current !== token) return;

      const data = response.data || [];
      const inc = response.included || [];

      if (pageNo === 1 || reset) {
        setListings(data);
        setIncluded(inc);
      } else {
        setListings((p) => [...p, ...data]);
        setIncluded((p) => [...p, ...inc]);
      }

      if (data.length === 0) setHasMore(false);
    } catch (e) {
      console.error("Failed to load listings:", e);
    } finally {
      if (latestRequestRef.current === token) {
        setLoading(false);
      }
    }
  };


  // -------------------------------------------------------
  // Trigger load when filterChanged or page changes
  // -------------------------------------------------------
  useEffect(() => {
    if (activeTab !== "find") return;

    if (filterChanged && !loading) {
      setFilterChanged(false);
      loadListings(1, {
        reset: true,
        filterSnapshot: findFilter,
        searchSnapshot: searchQuery,
      });
      return;
    }

    if (page > 1 && !loading && !filterChanged) {
      loadListings(page);
    }
  }, [filterChanged, page, activeTab, loading, findFilter, searchQuery]);


  // -------------------------------------------------------
  // Safe infinite scroll
  // -------------------------------------------------------
  useEffect(() => {
    if (activeTab !== "find") return;

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (
        first.isIntersecting &&
        hasMore &&
        !loading &&
        !filterChanged &&
        latestRequestRef.current !== 0
      ) {
        setPage((prev) => prev + 1);
      }
    });

    const el = loaderRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [activeTab, hasMore, loading, filterChanged]);


  // -------------------------------------------------------
  // Warning toast for extremely fast switching
  // -------------------------------------------------------
  const [listWarningShown, setListWarningShown] = useState(false);

  useEffect(() => {
    const warn =
      activeTab === "find" &&
      listings.length === 0 &&
      !loading &&
      !hasMore;

    if (warn && !listWarningShown) {
      setListWarningShown(true);

      window.dispatchEvent(
        new CustomEvent("global-toast", {
          detail: {
            message: "Please wait a few seconds… loading results.",
            type: "warning",
          },
        })
      );

      setTimeout(() => setListWarningShown(false), 2500);
    }
  }, [activeTab, listings, loading, hasMore, listWarningShown]);


  // -------------------------------------------------------
  // Add connection
  // -------------------------------------------------------
  const handleAddUser = async (swimmer: any) => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setDialogMessage("You must be logged in.");
        setShowDialog(true);
        return;
      }

      const mySwimmerId = Number(localStorage.getItem("current_user_swimmer_id"));
      const myCoachId = Number(localStorage.getItem("coach_id"));
      const friendId = Number(swimmer.id);

      let response = null;

      if (userType === "swimmer") {
        response = await repo.swimmerConnectToSwimmer(mySwimmerId, friendId);
      } else {
        response = await repo.coachConnectToSwimmer(myCoachId, friendId);
      }

      if (response?.status === 1) {
        setDialogMessage("Connected successfully.");
      } else if (response?.status === 2) {
        setDialogMessage(response.msg || "Connection already exists.");
      } else {
        setDialogMessage(response.msg || "Failed to connect.");
      }

      setShowDialog(true);
    } catch (err) {
      setDialogMessage("Something went wrong while connecting.");
      setShowDialog(true);
    }
  };

  useEffect(() => {
    if (activeTab === "find") setSearchQuery("");
  }, [findFilter, activeTab]);

// -------------------------------------------------------
// Render
// -------------------------------------------------------

return (    
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold border-b pb-3">
        {tabTitles[activeTab] || "Share"}
      </h1>

      {activeTab === "team" && <TeamsSection />}

      {activeTab === "feed" && (
        <div className="relative mt-4">
          <FeedList
            currentUserId={current_user_id}
            reloadKey={forceReloadFeed}
            onAddPost={() => setShowPostDialog(true)}
          />

          <AddPostDialog
            open={showPostDialog}
            onOpenChange={setShowPostDialog}
            onSubmit={handleCreatePost}
          />
        </div>
      )}

      {activeTab === "find" && (
        <>
          <div className="flex space-x-2">
            <Button
              variant={findFilter === "coaches" ? "default" : "outline"}
              onClick={() => setFindFilter("coaches")}
            >
              Coaches
            </Button>

            <CoachProfile
              open={profileOpen}
              onOpenChange={setProfileOpen}
              coach={selectedCoach}
            />

            <Button
              variant={findFilter === "products" ? "default" : "outline"}
              onClick={() => setFindFilter("products")}
            >
              Products
            </Button>

            <Button
              variant={findFilter === "swimmers" ? "default" : "outline"}
              onClick={() => setFindFilter("swimmers")}
            >
              Swimmers
            </Button>
          </div>

            <div className="mt-4">
              <Input
                placeholder={`Search ${findFilter}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

          <div className="mt-4 space-y-4">
            {loading && listings.length === 0 && (
              <div className="fixed inset-0 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            )}


            {findFilter === "swimmers" &&
              dedupeById(listings).map((s) => (
                  <SwimmerCard
                    key={s.id}
                    swimmer={s}
                    connectedIds={connectedIds}
                    onAdd={handleAddUser}
                  />
                ))}


            {findFilter === "coaches" &&
              listings.map(
                (l) =>
                  l.attributes && (
                    <CoachCard
                        key={l.id}
                        listing={l}
                        included={included}
                        onViewProfile={(coach: CoachProfileData) => {
                        setSelectedCoach(coach);
                        setProfileOpen(true);
                      }}
                      />
                  )
              )}

            {findFilter === "products" &&
              listings.map(
                (l) =>
                  l.attributes && (
                    <ProductCard key={l.id} listing={l} included={included} />
                  )
              )}

            <div ref={loaderRef} className="h-10" />

            {loading && listings.length > 0 && (
              <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            </div>
            )}
          </div>
        </>
      )}

      {/* {activeTab === "messages" && <MessagesPage />} */}
        {activeTab === "messages" && (
          <MessagesPage key="messages-root" />
        )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Notification</DialogTitle>
          <p className="text-muted-foreground mt-2">{dialogMessage}</p>

          <div className="mt-4 flex justify-end">
            <Button onClick={() => setShowDialog(false)}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>


      
    </div>
  );
}

